import { CookieService } from '@/app_lib/CookieService';
import { pianoHandlers } from '@/app_lib/services/Piano/pianoHandlers';
import { pianoTemplateTrackingEvent } from '@/app_lib/services/Piano/pianoTracking';
import {
  APPLE_MERCHANT_ID,
  PIANO_AID,
  PIANO_COMPOSER_HOST,
  PIANO_ENDPOINT,
  PIANO_ID_URL,
  PIANO_SANDBOX,
  PIANO_SITE_ID,
  PIANO_STATIC_DOMAIN,
} from '@/app_lib/services/tinypass/service';
import type { LinkedTerm, PianoPageConfig, ResponseVariables } from '@/app_lib/types/accounts/piano/Piano';
import type { Tinypass } from '@/app_lib/types/accounts/piano/tinypass';
import type { EventData } from '@/app_lib/types/Gtm';
import { isSubscribePage } from '@/app_lib/utils/clientUtils';
import { updateLocalStorageObject } from '@/app_lib/utils/localStorageUtils';

function onLinkedTermSelected(sessionId: string, linkedTerm: LinkedTerm) {
  const {
    linkedTermData: { externalTermId },
  } = linkedTerm;
  CookieService.set('pianoCheckoutSessionId', sessionId, { expires: 1 });
  window.location.href = `/next/checkout/${externalTermId}`;
}

function passwordReset(tp: Tinypass) {
  if (!tp.user.isUserValid()) {
    const tokenMatch = location.search.match(/reset_token=([A-Za-z0-9]+)/);
    if (tokenMatch) {
      const token = tokenMatch[1];
      tp.pianoId.show({
        loggedIn: () => {
          window.location.reload();
        },
        resetPasswordToken: token,
      });
    }
  }
}

/**
 * Push configs to the Piano object
 *
 * @param tp
 */
function pushConfigs(propTp: Tinypass) {
  const domain = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const tp = propTp;
  tp.push(['setAid', PIANO_AID]);
  tp.push(['setCxenseSiteId', PIANO_SITE_ID]);
  tp.push(['setSandbox', PIANO_SANDBOX]);
  tp.push(['setCookieDomain', domain]);
  tp.push(['setUsePianoIdUserProvider', true]);
  tp.push(['setUseTinypassAccounts', false]);
  tp.push(['setApplePayMerchantId', APPLE_MERCHANT_ID]);
  // tp.push(['setDebug', PIANO_SANDBOX]);

  if (!PIANO_SANDBOX && PIANO_AID === 'cfQj2fM3zj') {
    tp.push(['setPianoIdUrl', PIANO_ID_URL]);
    tp.push(['setComposerHost', PIANO_COMPOSER_HOST]);
    tp.push(['setEndpoint', PIANO_ENDPOINT]);
    tp.push(['setStaticDomain', PIANO_STATIC_DOMAIN]);
  }

  tp.push(['setLinkedTermSelected', onLinkedTermSelected]);
  tp.push(['setRequestUserAuthForLinkedTerm', false]);

  return tp;
}

function pushHandlers(
  propTp: Tinypass,
  updateResponseVariables: (responseVariables: Partial<ResponseVariables>) => void,
  sendEventOnConsent: (eventData: EventData) => void,
) {
  const tp = propTp;
  const handlers = pianoHandlers(tp, updateResponseVariables, sendEventOnConsent);
  (Object.keys(handlers) as Array<keyof typeof handlers>).forEach((name) => {
    tp.push(['addHandler', name, handlers[name]]);
  });

  return tp;
}

/**
 * Run to initalize Piano
 * See: https://docs.piano.io/track/implementing-piano#implementsinglepage
 *
 * @param pianoPageConfig
 */
export function pianoInit(
  pianoPageConfig: PianoPageConfig,
  isBypassed: boolean,
  updateResponseVariables: (responseVariables: Partial<ResponseVariables>) => void,
  sendEventOnConsent: (eventData: EventData) => void,
) {
  try {
    if (!window) {
      throw new Error('Cannot initialize tinypass, window is not defined');
    }
    let tp: Tinypass = window.tp || ([] as unknown as Tinypass);
    tp = pushConfigs(tp);
    tp = pushHandlers(tp, updateResponseVariables, sendEventOnConsent);
    tp = pianoPushPageData(tp, pianoPageConfig, isBypassed);
    tp.push([
      'init',
      () => {
        // tp methods are called within the init callback
        passwordReset(tp);
        tp.pianoId.init();
        tp.experience.init();
      },
    ]);
    window.addEventListener('message', (event) => {
      const subscribePage = isSubscribePage();
      // Generate and store custom referral params from custom event sent from Piano offer template used
      // on non-subscribe pages
      if (event.data.eventName === 'customOfferTrackingParams' && !subscribePage) {
        // NOTE: Do we neeed to check persistReferralParams or do we always want last one of these to rule?
        const itmSource = event.data.params?.itmSource;
        const encodedUrl = encodeURIComponent(window.location.href);
        const referralParams = `itm_source=${itmSource}&redirect=${encodedUrl}`;
        updateLocalStorageObject('checkoutTrackingProps', {
          // biome-ignore lint/style/useNamingConvention: it's just the way it is
          checkout_referral_params: referralParams || null,
          persistReferralParams: true,
        });
      }
      if (event.data.eventName === 'pianoTemplateTrackingEvent') {
        pianoTemplateTrackingEvent(tp, event.data, sendEventOnConsent);
      }
    });
    return true;
  } catch {
    // console.error('Error initializing Piano', e);
    return false;
  }
}
export function pianoPushPageData(
  propTp: Tinypass,
  { contentSection, tags, publishDate }: PianoPageConfig,
  isBypassed = false,
) {
  const section = isBypassed ? 'Editorial Bypass' : contentSection;
  const tp = propTp;
  tp.push(['setContentSection', section]);
  tp.push(['setContentCreated', publishDate]);
  tp.push(['setPageURL', window.location.href]);
  tp.push(['setTags', tags]);
  // Postlaunch TODO:
  // tp.push(['setCustomVariable', 'contentType', channel]); // might be useful for bucketing page types
  return tp;
}
